import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideRouter, RouteReuseStrategy } from '@angular/router';
import { filter, firstValueFrom, take, timeout } from 'rxjs';
import { appRoutes } from './app.routes';
import { TabViewRouteReuseStrategy } from './shared/tab-view/tab-view-route-reuse-strategy';
import { provideEchartsCore } from 'ngx-echarts';
import { langHeaderInterceptor } from './interceptors/lang-header.interceptor';
import { projectHeaderInterceptor } from './interceptors/project-header.interceptor';
import { provideAuth0, authHttpInterceptorFn } from '@auth0/auth0-angular';
import { environment } from '../environments/environment';
import { authErrorInterceptor } from './interceptors/auth-error.interceptor';
import { AuthErrorHandler } from './auth/auth-error-handler';
import { apiErrorInterceptor } from './interceptors/api-error.interceptor';
import { I18nService } from './shared/i18n/i18n.service';
import { provideAppDateAdapter } from './shared/date/app-material-date';

const API_ERRORS_SCOPE = 'api_errors';
const I18N_READY_TIMEOUT_MS = 5000;

// For scenarios where we need to load translations before the app is usable (e.g. API error messages)
// there is a very small chance that loading the translations may fail or hang, to prevent whole app from crashing there is a 5 second timeout
function i18nInitializer(i18n: I18nService): () => Promise<void> {
  return async () => {
    if (!i18n.ready()) {
      try {
        await firstValueFrom(
          toObservable(i18n.ready).pipe(
            filter((ready) => ready),
            take(1),
            timeout({ first: I18N_READY_TIMEOUT_MS }),
          ),
        );
      } catch (error: unknown) {
        console.warn(
          `i18n readiness timed out after ${I18N_READY_TIMEOUT_MS}ms; continuing bootstrap with fallback translations.`,
          error,
        );
      }
    }
    try {
      // promise.race returns a single promise that settles as soon as the first promise in the list settles
      // either the timeout or scope loaded
      await Promise.race([
        i18n.loadScope(API_ERRORS_SCOPE),
        timeoutPromise(I18N_READY_TIMEOUT_MS),
      ]);
    } catch (error: unknown) {
      console.warn(
        `i18n scope "${API_ERRORS_SCOPE}" did not load in ${I18N_READY_TIMEOUT_MS}ms; continuing bootstrap with fallback translations.`,
        error,
      );
    }
  };
}

function timeoutPromise(timeoutMs: number): Promise<never> {
  return new Promise((_, reject) => {
    globalThis.setTimeout(() => {
      reject(new Error(`Timeout after ${timeoutMs}ms`));
    }, timeoutMs);
  });
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideAppInitializer(() => i18nInitializer(inject(I18nService))()),
    provideRouter(appRoutes),
    { provide: RouteReuseStrategy, useClass: TabViewRouteReuseStrategy },
    provideEchartsCore({ echarts: () => import('echarts') }),
    // Platform-wide dd/MM/yyyy datepicker display/parse (see provideAppDateAdapter).
    provideAppDateAdapter(),
    { provide: ErrorHandler, useClass: AuthErrorHandler },
    provideHttpClient(
      withInterceptors([
        authErrorInterceptor,
        authHttpInterceptorFn,
        langHeaderInterceptor,
        projectHeaderInterceptor,
        apiErrorInterceptor,
      ]),
    ),
    provideAuth0({
      domain: environment.auth0.domain,
      clientId: environment.auth0.clientId,
      authorizationParams: {
        redirect_uri: globalThis.window.location.origin,
        scope: environment.auth0.authorizationParams.scope,
        audience: environment.auth0.authorizationParams.audiences.api, // initial/default audience
      },
      cacheLocation: 'localstorage',
      useRefreshTokens: true,
      httpInterceptor: {
        allowedList: [
          {
            uri: `${environment.backendUrls.api}/*`,
            tokenOptions: {
              authorizationParams: {
                audience: environment.auth0.authorizationParams.audiences.api,
              },
            },
          },
        ],
      },
    }),
  ],
};
